import { Fragment, type ComponentType, type ReactNode, useEffect, useState } from "react";
import { BarChart3, Workflow } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { signOut } from "../auth/client";
import { useCurrentUser } from "../auth/me";
import { useDocumentTitle } from "../lib/document-title";
import { PageReveal } from "../lib/motion";
import { useTheme } from "../lib/theme-store";
import { useBreadcrumb, useBreadcrumbTitle } from "./breadcrumbs";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@akaike/ui";
import {
  IconBolt,
  IconBrands,
  IconClose,
  IconCompare,
  IconJobs,
  IconLogout,
  IconMenu,
  IconMoon,
  IconPlatforms,
  IconProxies,
  IconWaf,
  IconRuns,
  IconSettings,
  IconStatus,
  IconStores,
  IconSun,
  IconUsers,
} from "./icons";

type NavItem = {
  to: string;
  label: string;
  Icon: ComponentType;
  superuser?: boolean;
};

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Overview",
    items: [
      { to: "/status", label: "Platform status", Icon: IconStatus },
      { to: "/platforms", label: "Platforms", Icon: IconPlatforms },
    ],
  },
  {
    group: "Scraping",
    items: [
      { to: "/jobs", label: "Jobs", Icon: IconJobs },
      { to: "/runs", label: "Runs", Icon: IconRuns },
      { to: "/availability", label: "Availability", Icon: Workflow },
      { to: "/compare", label: "Compare runs", Icon: IconCompare },
    ],
  },
  {
    group: "Catalog",
    items: [
      { to: "/brands", label: "Brands", Icon: IconBrands },
      // Beside Brands, because it answers the same operator question: what is a share a
      // share OF. Split across Brands and Platforms, nobody finds the second half.
      { to: "/rank", label: "Rank", Icon: BarChart3 },
      { to: "/stores", label: "Stores", Icon: IconStores },
      { to: "/taxonomy", label: "Taxonomy", Icon: IconPlatforms },
      { to: "/snapshots", label: "Snapshots", Icon: IconStores },
    ],
  },
  {
    group: "Infra",
    items: [
      { to: "/proxies", label: "Proxies", Icon: IconProxies },
      { to: "/waf", label: "WAF", Icon: IconWaf },
    ],
  },
  {
    group: "Admin",
    items: [
      { to: "/users", label: "Users", Icon: IconUsers, superuser: true },
      { to: "/settings", label: "Settings", Icon: IconSettings },
    ],
  },
];

// Pure data-table routes get the full content width (no 1680 cap) so wide tables can
// use the horizontal space instead of scrolling. Pages that mix KPI cards with a table
// stay capped so the cards don't balloon on wide screens.
const WIDE_ROUTES = ["/runs", "/availability", "/taxonomy/review"];

export function initials(name: string | null, email: string): string {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return `${now.toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour12: false })} IST`;
}

export function Shell({ children }: { children: ReactNode }) {
  const me = useCurrentUser();
  const { pathname } = useLocation();
  const tail = useBreadcrumb();
  const titleParts = useBreadcrumbTitle();
  const { dark, toggleTheme } = useTheme();
  const clock = useClock();
  // Off-canvas nav drawer (mobile/tablet only; CSS shows it below the shell
  // breakpoint). Closes whenever the route changes so a nav tap dismisses it.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  const groups = NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.superuser || me.is_superuser),
  })).filter((g) => g.items.length > 0);

  const isWide = WIDE_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));

  const flat = groups.flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })));
  const active =
    flat.find((i) => i.to === pathname) ??
    // nested routes (e.g. /runs/:id) light up their section parent
    flat.find((i) => i.to !== "/" && pathname.startsWith(`${i.to}/`)) ?? {
      group: "OVERVIEW",
      label: "Platform status",
      to: "/status",
    };

  // Tab title follows the breadcrumb, most-specific crumb first so it survives
  // truncation: `Catalog / Stores / 30995` -> "30995 - Stores - qcom_scrape".
  // A page can supply its own ordering (see `useBreadcrumbTail`).
  useDocumentTitle([...(titleParts ?? tail.map((c) => c.label).reverse()), active.label]);

  return (
    <div className={`app${navOpen ? " nav-open" : ""}`}>
      <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      <aside className="sidebar">
        <div className="sidebar-brand">
          <span className="glyph">
            <span style={{ color: "var(--background)" }}>
              <IconBolt />
            </span>
          </span>
          <div className="col">
            <span className="wm">
              qcom<b>-</b>scrape
            </span>
          </div>
          {/* 900px mirrors the qcs.css drawer breakpoint: below it the sidebar
              is off-canvas, so these two controls are the only way to reach the nav. */}
          <Button
            className="hidden max-[900px]:ml-auto max-[900px]:inline-flex"
            variant="outline"
            size="sm"
            icon
            onClick={() => setNavOpen(false)}
            title="Close menu"
            aria-label="Close menu"
          >
            <IconClose />
          </Button>
        </div>

        {groups.map((g) => (
          <div className="nav-group" key={g.group}>
            <div className="eyebrow" style={{ padding: "0 9px 6px" }}>
              {g.group}
            </div>
            {g.items.map(({ to, label, Icon }) => {
              const isActive = pathname === to || (to !== "/" && pathname.startsWith(`${to}/`));
              return (
                <Link key={to} to={to} className={`nav-item ${isActive ? "active" : ""}`}>
                  <Icon />
                  <span>{label}</span>
                </Link>
              );
            })}
          </div>
        ))}

        <div className="sidebar-spacer" />
        <div className="sidebar-user">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="user-chip as-btn focus-visible:outline-hidden">
                <span className="avatar">{initials(me.name, me.email)}</span>
                <div className="who">
                  <b>{me.email}</b>
                  <span>{me.role}</span>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" sideOffset={8}>
              <DropdownMenuItem variant="destructive" onSelect={() => void signOut()}>
                <IconLogout />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {/* 900px mirrors the qcs.css drawer breakpoint: below it the sidebar
              is off-canvas, so these two controls are the only way to reach the nav. */}
          <Button
            className="hidden max-[900px]:inline-flex max-[900px]:flex-none"
            variant="outline"
            size="sm"
            icon
            onClick={() => setNavOpen(true)}
            title="Open menu"
            aria-label="Open menu"
          >
            <IconMenu />
          </Button>
          <div className="crumbs">
            <span>{active.group.charAt(0) + active.group.slice(1).toLowerCase()}</span>
            <span className="sep">/</span>
            {/* the section is the current page when there's no detail tail; otherwise
                it links back to its list and the tail crumbs trail after it */}
            {tail.length === 0 ? (
              <b>{active.label}</b>
            ) : (
              <Link to={active.to}>{active.label}</Link>
            )}
            {tail.map((c, i) => {
              const last = i === tail.length - 1;
              return (
                <Fragment key={`${c.label}-${i}`}>
                  <span className="sep">/</span>
                  {c.to && !last ? <Link to={c.to}>{c.label}</Link> : <b>{c.label}</b>}
                </Fragment>
              );
            })}
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={toggleTheme} title="Toggle light and dark">
            {dark ? <IconSun /> : <IconMoon />}
          </Button>
          <div className="clock">{clock}</div>
        </header>

        <main className={`view${isWide ? " view--wide" : ""}`}>
          {/* calm route reveal keyed on the top-level path segment, so a
              param-only change within a section (e.g. /runs/:id) updates in
              place without remounting. Reduced-motion falls back to instant. */}
          <PageReveal routeKey={pathname.split("/")[1] || "root"}>{children}</PageReveal>
        </main>
      </div>
    </div>
  );
}
